import React, { useEffect, useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Card } from '../../../components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';

const STORAGE_KEY = 'adminDuplicatesInfoOpen';

/** Объяснение для админа простыми словами: что такое дубль и что делает склейка. */
export function InfoCard({ isDark }: { isDark: boolean }) {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === null ? true : saved === '1';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, open ? '1' : '0');
    } catch {
      /* без localStorage просто не запоминаем */
    }
  }, [open]);

  const text = isDark ? 'text-slate-300' : 'text-slate-700';
  const heading = cn('font-semibold mb-1', isDark ? 'text-white' : 'text-[#111111]');

  return (
    <Card
      className={cn(
        'rounded-2xl border-0 overflow-hidden',
        isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
      )}
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              'w-full flex items-center gap-3 p-5 text-left',
              isDark ? 'hover:bg-[#20202A]' : 'hover:bg-[#FBFAF7]',
            )}
          >
            <div className="w-9 h-9 rounded-xl bg-sky-500/10 flex items-center justify-center shrink-0">
              <Info className="h-5 w-5 text-sky-500" />
            </div>
            <div className="flex-1">
              <p className={cn('font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>
                Как работают дубли и склейка
              </p>
              <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                Прочитайте один раз перед первой склейкой
              </p>
            </div>
            <ChevronDown
              className={cn(
                'h-5 w-5 transition-transform',
                open && 'rotate-180',
                isDark ? 'text-slate-400' : 'text-slate-500',
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className={cn('grid gap-5 md:grid-cols-2 px-5 pb-5 text-sm leading-relaxed', text)}>
            <div>
              <p className={heading}>Что такое дубль</p>
              <p>
                Человек вошёл через Telegram и получил <b>новый пустой аккаунт</b>, хотя у него уже
                был старый (через email, Google или Apple) — часто с премиумом и прогрессом. Telegram
                к старому аккаунту не был привязан, поэтому система не узнала человека. Новый аккаунт
                — <b>дубль</b>, старый — <b>основной</b>.
              </p>
            </div>
            <div>
              <p className={heading}>Что делает склейка</p>
              <p>
                Переносит вход через Telegram и номер телефона с дубля на основной аккаунт. После
                этого человек, входя через Telegram, попадает в свой старый аккаунт. Дубль{' '}
                <b>не удаляется</b>: он помечается «склеен» и больше не может входить. Прогресс
                дубля (решённые билеты, попытки) <b>не переносится</b> — остаётся в архиве на дубле.
                Кнопкой склейку не отменить.
              </p>
            </div>
            <div>
              <p className={heading}>Почему оплаты блокируют склейку</p>
              <p>
                Если на дубле были оплаты или подписка, деньги и премиум привязаны к нему, а
                склейка их не переносит. Такие случаи решаем вручную: вернуть деньги или продлить
                премиум на основном аккаунте — и только потом разбираться с дублем.
              </p>
            </div>
            <div>
              <p className={heading}>Откуда «Возможные дубли»</p>
              <p>
                Когда на том же телефоне раньше был вход в один аккаунт, а потом через Telegram
                создан другой, система запоминает эту пару. Это <b>подсказка, а не факт</b>:
                телефоном могли пользоваться двое (например, брат и сестра). Сверьте данные и решите
                сами — «Склеить…» или «Не дубль».
              </p>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
